// Force hard refresh after hook modifications
// console filter and error suppressor temporarily disabled for boot debugging
import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { ensureInertSupport } from './utils/accessibility'

console.log('🚀 Main.tsx loaded, creating root...');

// 🔧 Ensure console methods are always callable (prevent React warnings crash)
['error','warn','log','info','debug','trace'].forEach((m) => {
  const anyConsole: any = console as any;
  if (typeof anyConsole[m] !== 'function') {
    anyConsole[m] = function(){};
  }
});


// 🔒 Global error handler + automatic cache repair for broken bundles
function isRecoverableInitError(err: any) {
  const msg = String((err && (err.message || err)) || '');
  return (
    msg.includes("Cannot access 'Me' before initialization") ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  );
}

async function repairCachesAndReload(context: string) {
  if (sessionStorage.getItem('lovable_cache_repair_done')) return;
  sessionStorage.setItem('lovable_cache_repair_done', '1');
  console.warn(`🧹 Starting cache repair due to ${context}...`);
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch (e) {
    console.warn('Cache repair encountered an issue:', e);
  }
  const url = new URL(window.location.href);
  url.searchParams.set('v', Date.now().toString());
  window.location.replace(url.toString());
}

window.addEventListener('error', (event) => {
  console.error('🚨 GLOBAL ERROR:', event.error);
  if (isRecoverableInitError(event.error || event.message)) {
    event.preventDefault?.();
    repairCachesAndReload('global error');
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('🚨 UNHANDLED REJECTION:', event.reason);
  if (isRecoverableInitError(event.reason)) {
    event.preventDefault?.();
    repairCachesAndReload('unhandled rejection');
  }
});

// Initialize accessibility utilities
ensureInertSupport();

const rootElement = document.getElementById("root");
console.log('📍 Root element found:', !!rootElement);

if (rootElement) {
  try {
    const root = createRoot(rootElement);
    console.log('🎯 React root created, rendering app...');
    root.render(<App />);
    console.log('✅ App rendered successfully');
  } catch (error) {
    console.error('❌ CRITICAL: Failed to render app:', error);
    // Mostrar mensaje de error en caso de fallo crítico
    rootElement.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; background: #0f0f0f; color: #fff; font-family: system-ui;">
        <div style="text-align: center; max-width: 500px;">
          <h1 style="font-size: 24px; margin-bottom: 16px;">Error Crítico</h1>
          <p style="margin-bottom: 24px; color: #aaa;">La aplicación no pudo cargar correctamente. Por favor, recarga la página o limpia los datos del navegador.</p>
          <button onclick="window.location.reload()" style="padding: 12px 24px; background: #4f46e5; border: none; border-radius: 6px; color: white; cursor: pointer; font-size: 16px;">
            Recargar Página
          </button>
          <button onclick="localStorage.clear(); window.location.reload()" style="padding: 12px 24px; background: #dc2626; border: none; border-radius: 6px; color: white; cursor: pointer; font-size: 16px; margin-left: 8px;">
            Limpiar Datos y Recargar
          </button>
        </div>
      </div>
    `;
  }
} else {
  console.error('❌ Root element not found!');
}
